import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/react-query/queryKeys";
import { useBoards } from "@/features/curriculum/hooks/useBoards";
import { useSubjectLookup } from "@/features/curriculum/hooks/useSubjectLookup";
import {
  fetchClassOptions,
  fetchSectionOptions,
  fetchStreamOptions,
  mapBoardOptions,
  type LookupOption,
  type SectionOption,
} from "../services/studentLookupService";
import {
  fetchInstitutionOptions,
  type StudentInstitutionOption,
} from "../services/studentInstitutionOptionsService";

/** Common shape the dropdown fields read from a lookup. */
export type LookupState<T extends LookupOption> = {
  options: T[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => unknown;
};

export const LOOKUP_STALE_TIME = 1000 * 60 * 10;

/**
 * Query definitions of the class / section lookups, shared with the edit page, which warms
 * the same cache entries while it resolves the saved board / class / section.
 */
export const classOptionsQuery = (boardId: string) => ({
  queryKey: queryKeys.students.classOptions(boardId || undefined),
  queryFn: () => fetchClassOptions(boardId),
  staleTime: LOOKUP_STALE_TIME,
});
export const sectionOptionsQuery = (instituteId: string, classId: string) => ({
  queryKey: queryKeys.students.sectionOptions(
    instituteId || undefined,
    classId || undefined,
  ),
  queryFn: () => fetchSectionOptions({ instituteId, classId }),
  staleTime: LOOKUP_STALE_TIME,
});

/** Institute dropdown options of the add-student form. */
export function useStudentInstitutionOptions(options?: { enabled?: boolean }) {
  return useQuery<StudentInstitutionOption[]>({
    queryKey: queryKeys.students.institutionOptions(),
    queryFn: fetchInstitutionOptions,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Board dropdown options (api/v1/metadata/boards via the shared useBoards query).
 * A non-empty `instituteId` is sent as the `institute_id` query param.
 */
export function useStudentBoardOptions(
  instituteId?: string,
): LookupState<LookupOption> {
  const query = useBoards(undefined, { instituteId });
  const options = useMemo(() => mapBoardOptions(query.data), [query.data]);
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/**
 * Subjects of the selected board + class (api/v1/helpers/get-subjects via the shared
 * useSubjectLookup query); idle until both are chosen.
 */
export function useSubjectOptions(
  boardId: string,
  classId: string,
): LookupState<LookupOption> {
  const query = useSubjectLookup(boardId || undefined, classId || undefined);
  const options = useMemo(
    () =>
      (query.data?.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [query.data],
  );
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/** Classes of the selected board; idle until a board is chosen. */
export function useStudentClassOptions(
  boardId: string,
): LookupState<LookupOption> {
  const query = useQuery<LookupOption[]>({
    ...classOptionsQuery(boardId),
    enabled: !!boardId,
    refetchOnWindowFocus: false,
  });
  const options = useMemo(() => query.data ?? [], [query.data]);
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/** Streams of the selected institute + board + class; idle until all three are chosen. */
export function useStudentStreamOptions(
  instituteId: string,
  boardId: string,
  classId: string,
): LookupState<LookupOption> {
  const query = useQuery<LookupOption[]>({
    queryKey: queryKeys.students.streamOptions(
      instituteId || undefined,
      boardId || undefined,
      classId || undefined,
    ),
    queryFn: () => fetchStreamOptions({ instituteId, boardId, classId }),
    staleTime: LOOKUP_STALE_TIME,
    enabled: !!instituteId && !!boardId && !!classId,
    refetchOnWindowFocus: false,
  });
  const options = useMemo(() => query.data ?? [], [query.data]);
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/**
 * Sections of the selected class at the selected institute (the class id is the
 * board_class_id); idle until both an institute and a class are chosen.
 */
export function useStudentSectionOptions(
  instituteId: string,
  classId: string,
): LookupState<SectionOption> {
  const query = useQuery<SectionOption[]>({
    ...sectionOptionsQuery(instituteId, classId),
    enabled: !!instituteId && !!classId,
    refetchOnWindowFocus: false,
  });
  const options = useMemo(() => query.data ?? [], [query.data]);
  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
